import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setLocalError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-12">
            <img 
              src="/betherapy-logo.png" 
              alt="Betherapy" 
              className="h-14 w-auto"
            />
          </Link>

          <div className="animate-fade-in">
            <h1 className="text-3xl font-light text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Bem-vindo de volta
            </h1>
            <p className="text-[#63635E] mb-8">
              Entre na sua conta para continuar sua jornada
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              {(localError || error) && (
                <div className="p-4 rounded-xl bg-[#B85C5C]/10 text-[#B85C5C] text-sm" data-testid="login-error">
                  {localError || error}
                </div>
              )}

              {/* Google Login Button */}
              {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <button
                type="button"
                onClick={() => {
                  const redirectUrl = window.location.origin + '/dashboard';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border-2 border-[#EAE8E3] bg-white hover:bg-[#F9F8F6] text-[#292927] font-medium transition-colors"
                data-testid="google-login-button"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Entrar com Google
              </button>

              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-[#EAE8E3]" />
                <span className="text-xs text-[#A0A09A]">ou</span>
                <div className="flex-1 h-px bg-[#EAE8E3]" />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#292927] mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0A09A]" strokeWidth={1.5} />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-theraflow pl-12"
                    placeholder="seu@email.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-[#292927] mb-2">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0A09A]" strokeWidth={1.5} />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-theraflow pl-12 pr-12"
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A0A09A] hover:text-[#63635E]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Entrar'
                )}
              </button>
            </form>

            <p className="mt-8 text-center text-sm text-[#63635E]">
              Não tem uma conta?{' '}
              <Link to="/register" className="text-[#4A5D4E] font-medium hover:underline" data-testid="register-link">
                Criar conta
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/6560308/pexels-photo-6560308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Therapy room"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#4A5D4E]/20" />
        <div className="absolute bottom-0 left-0 right-0 p-12 bg-gradient-to-t from-black/50 to-transparent">
          <blockquote className="text-white">
            <p className="text-lg font-light italic mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              "O autoconhecimento é o primeiro passo para a transformação pessoal."
            </p>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
